import React from "react";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/20fe0d92-bbc7-4a3e-a773-b89883ef73fe/images/1a62eb9c54c7f4324da2e631592e63303f10878a8c38717d7f5f7275266abdd4.png";

const Hero = () => {
  const { t } = useI18n();
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden grain-bg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold uppercase tracking-[0.18em] mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" />
              {t.hero.eyebrow}
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold text-slate-900">
              {t.hero.title1}
              <br />
              <span className="text-sky-600">{t.hero.title2}</span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
              {t.hero.subtitle}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("contact")}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-7 h-14 text-base font-medium group"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("services")}
                variant="outline"
                className="bg-white border-slate-300 text-slate-900 hover:bg-slate-50 rounded-full px-7 h-14 text-base font-medium"
              >
                {t.hero.ctaSecondary}
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
              {[t.hero.microPoint1, t.hero.microPoint2, t.hero.microPoint3].map((m) => (
                <div key={m} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 grid place-items-center">
                    <Check className="w-3 h-3 text-emerald-700" />
                  </span>
                  <span>{m}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-in">
            <div className="relative">
              <div className="absolute -inset-8 bg-gradient-to-tr from-sky-200/40 via-transparent to-transparent rounded-3xl blur-2xl" />
              <div className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <img
                  src={HERO_IMG}
                  alt="AI automation visual"
                  className="w-full h-auto object-cover animate-float"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-xl p-4 shadow-lg hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500" />
                  <div>
                    <div className="text-xs text-slate-500">+ Cita confirmada</div>
                    <div className="text-sm font-semibold text-slate-900">11:30 · Dr. Ruiz</div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-lg hidden md:block">
                <div className="text-xs text-slate-500">ROI mensual</div>
                <div className="text-xl font-display font-bold text-sky-600 tabular">+512%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
